import React, {
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Modifier, usePopper } from 'react-popper';

import { Placement } from '@popperjs/core';
import { animated, useSpring } from '@react-spring/web';
import { styled } from '@spazious/dollhouse-theme';
import { useForkRef } from '@spazious/react-hooks';

import { useClickAway } from '../../hooks/index.js';

import { TooltipArrow, TooltipArrowElement } from './TooltipArrow.js';
import {
  TooltipContainer,
  TooltipContainerElement,
  TooltipContainerProps,
} from './TooltipContainer.js';
import { TooltipContent } from './TooltipContent.js';

const AnimatedTooltipContainer = animated(TooltipContainer);
export interface TooltipProps extends Omit<TooltipContainerProps, 'content' | 'children'> {
  children?:
    | React.ReactNode
    | ((props: { open: boolean; setOpen: (open: boolean) => void }) => React.ReactNode);
  content?: React.ReactNode;
  /**
   * The number of milliseconds to wait before showing the tooltip.
   * @default 1000
   */
  showThreshold?: number;
  /**
   * The number of milliseconds to wait before hiding the tooltip after it has been trigger to hide.
   * @default 300
   */
  hideThreshold?: number;
  /**
   * The event that triggers the tooltip.
   * @default hover
   *
   */
  trigger?: 'hover' | 'click';
  /**
   * Whether the tooltip is mounted.
   * @default true
   */
  mounted?: boolean;
  /**
   * The strategy to use when positioning the tooltip.
   */
  strategy?: 'fixed' | 'absolute';
  /**
   * The placement of the tooltip.
   */
  placement?: Placement;
}

/**
 * Binds an event handler to a side effect. The side effect is executed before the event handler.
 */
const bindSideEffectEvent =
  <T, E extends UIEvent>(
    sideEffect: () => void,
    handler?: React.EventHandler<React.SyntheticEvent<T, E>>
  ) =>
  (evt: React.SyntheticEvent<T, E>) => {
    handler?.(evt);
    if (evt.defaultPrevented !== true) {
      sideEffect();
    }
  };

export const Tooltip = styled(
  forwardRef<TooltipContainerElement, TooltipProps>(
    (
      {
        children,
        content,
        showThreshold = 600,
        hideThreshold = 300,
        trigger = 'hover',
        strategy = 'absolute',
        mounted = false,
        placement = 'auto',
        ...restProps
      },
      ref
    ) => {
      const elementRef = useRef<TooltipContainerElement>(null);
      const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
      const [arrowElement, setArrowlement] = useState<TooltipArrowElement | null>(null);
      const [popperElement, setPopperElement] = useState<TooltipContainerElement | null>(null);

      const [open, setOpen] = useState(false);
      const [mount, setMount] = useState(false);
      const { styles, attributes } = usePopper(rootElement, popperElement, {
        strategy,
        placement,
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top', 'bottom'],
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 16],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              mainAxis: true,
              padding: 16,
            },
          },
          {
            name: 'arrow',
            options: {
              element: arrowElement,
            },
          },
          {
            name: 'arrowPlacement',
            enabled: true,
            phase: 'write',
            fn: useCallback<PickProp<Modifier<'arrow', {}>, 'fn'>>(({ state }) => {
              const { arrow: arrowElm } = state.elements;
              const { arrow: arrowData } = state.modifiersData;
              if (arrowElm) {
                arrowElm.setAttribute('data-popper-placement', state.placement);

                if (arrowData?.centerOffset !== 0) {
                  arrowElm.setAttribute('data-hide', 'true');
                } else {
                  arrowElm.removeAttribute('data-hide');
                }
              }
            }, []),
          },
        ],
      });

      useImperativeHandle(ref, () => elementRef.current!);

      const node = useMemo<React.ReactElement<{
        ref: React.Ref<HTMLElement>;
        onMouseOver: React.MouseEventHandler;
        onMouseLeave: React.MouseEventHandler;
        onMouseDown: React.MouseEventHandler;
        onClick: React.MouseEventHandler;
      }> | null>(() => {
        if (isValidElement(children)) {
          return children as any;
        } else if (typeof children === 'function') {
          return children({ open, setOpen });
        } else if (typeof children === 'string') {
          throw new Error('Tooltip children must be a valid React element or a function');
        }

        return null;
      }, [children, open]);

      const timerRef = useRef<NodeJS.Timeout | null>(null);

      const inmediateOpen = useCallback(() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        setMount(true);
        setOpen(true);
      }, []);

      const inmediateClose = useCallback(() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        setOpen(false);
      }, []);

      const startTimerToOpen = useCallback(() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        setMount(true);
        timerRef.current = setTimeout(() => {
          setOpen(true);
        }, showThreshold);
      }, [showThreshold]);

      const startTimerToClose = useCallback(() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          setOpen(false);
        }, hideThreshold);
      }, [hideThreshold]);

      useEffect(() => () => (timerRef.current && clearTimeout(timerRef.current)) as void, []);

      const { setNodeRef, preventDefault } = useClickAway(inmediateClose);
      const nodeForkedRef = useForkRef(setRootElement, elementRef) as React.Ref<HTMLElement>;
      const tooltipForkedRef = useForkRef<TooltipContainerElement>(
        setPopperElement as React.RefCallback<HTMLElement>,
        setNodeRef
      );

      const tooltipAttributes = useMemo(
        () => ({
          control:
            trigger === 'hover'
              ? {
                  onMouseOver: bindSideEffectEvent(startTimerToOpen, node?.props.onMouseOver),
                  onMouseLeave: bindSideEffectEvent(startTimerToClose, node?.props.onMouseLeave),
                  onMouseDown: bindSideEffectEvent(inmediateClose, node?.props.onMouseDown),
                }
              : {
                  onClick: bindSideEffectEvent(() => {
                    preventDefault();
                    !open ? inmediateOpen() : inmediateClose();
                  }, node?.props.onClick),
                },
          popper:
            trigger === 'hover'
              ? {
                  onMouseOver: bindSideEffectEvent(startTimerToOpen, restProps?.onMouseOver),
                  onMouseLeave: bindSideEffectEvent(startTimerToClose, restProps?.onMouseLeave),
                }
              : {},
        }),
        [
          inmediateClose,
          inmediateOpen,
          node?.props.onClick,
          node?.props.onMouseDown,
          node?.props.onMouseLeave,
          node?.props.onMouseOver,
          open,
          preventDefault,
          restProps?.onMouseLeave,
          restProps?.onMouseOver,
          startTimerToClose,
          startTimerToOpen,
          trigger,
        ]
      );

      const spring = useSpring({
        from: { opacity: 0 },
        to: { opacity: open ? 1 : 0 },
        onRest: () => {
          if (!open) {
            setMount(false);
          }
        },
      });

      if (!node) {
        return null;
      }

      return (
        <>
          <node.type {...node.props} ref={nodeForkedRef} {...tooltipAttributes.control} />
          {(mounted || mount) &&
            createPortal(
              <AnimatedTooltipContainer
                {...restProps}
                ref={tooltipForkedRef}
                style={{
                  ...styles.popper,
                  ...restProps.style,
                  opacity: spring.opacity,
                  visibility: spring.opacity.to((value) => (value === 0 ? 'hidden' : 'visible')),
                }}
                {...attributes.popper}
                {...tooltipAttributes.popper}
                data-show={open}
              >
                <TooltipArrow ref={setArrowlement} style={styles.arrow} {...attributes.arrow} />
                <TooltipContent>{content}</TooltipContent>
              </AnimatedTooltipContainer>,
              document.body as Element
            )}
        </>
      );
    }
  )
).withConfig({
  displayName: 'Tooltip',
})``;
